// Service accounts — org-owned, user-created machine identities. Unlike a
// personal API key (which authenticates "as me"), a service account is its
// own principal within a single organization: it is added as an org member,
// granted a role at creation, and can have any number of API keys minted for
// it. Those keys reuse the personal-API-key token shape (`rfx_...`) and
// resolve to the service account, so `x-organization-id` selects the org on
// each request exactly as it does for a human's key.
//
// The service account is backed by a `users` row (the same mechanism the
// plugin-seeded `svc-*` accounts use), so all downstream auth, RBAC, and
// resource-ownership machinery treats it uniformly. The `id` here is that
// backing user id.

import { z } from 'zod';
import { coreIds } from './ids.js';

/** Lifecycle status of a service account. Mirrors `users.status`. */
export const ServiceAccountStatusSchema = z.enum(['active', 'disabled']);
export type ServiceAccountStatus = z.infer<typeof ServiceAccountStatusSchema>;

/** A role assigned to the service account within its organization. */
export const ServiceAccountRoleSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type ServiceAccountRole = z.infer<typeof ServiceAccountRoleSchema>;

export const ServiceAccountSchema = z.object({
  id: coreIds.schema('serviceAccount'),
  name: z.string(),
  status: ServiceAccountStatusSchema,
  createdAt: z.number(),
  /** The user who created the account, or null if that user has been removed. */
  createdByUserId: z.string().nullable(),
  /** Org roles the account holds; its effective permissions are their union. */
  roles: z.array(ServiceAccountRoleSchema),
});
export type ServiceAccount = z.infer<typeof ServiceAccountSchema>;

export const CreateServiceAccountRequestSchema = z.object({
  name: z.string().trim().min(1).max(100),
  /**
   * Org role the account is granted at creation. Must be an org-scoped role
   * that exists for the caller's organization (a built-in role such as
   * `builtin-org-member` or a custom `rol_...` role). Validated server-side.
   */
  roleId: z.string().min(1),
});
export type CreateServiceAccountRequest = z.infer<typeof CreateServiceAccountRequestSchema>;

export const CreateServiceAccountResponseSchema = z.object({
  serviceAccount: ServiceAccountSchema,
});
export type CreateServiceAccountResponse = z.infer<typeof CreateServiceAccountResponseSchema>;

export const ServiceAccountsListResponseSchema = z.object({
  serviceAccounts: z.array(ServiceAccountSchema),
});
export type ServiceAccountsListResponse = z.infer<typeof ServiceAccountsListResponseSchema>;
